import { Button, Checkbox, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Textarea } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'
import { MdOutlineSend } from 'react-icons/md'
import { SAFETY, TONES } from '../constants'
import { useLogisticBox } from '../context/LogisticBoxContext'
import { useSelectedSeller } from '../hooks/useSelectedSeller'
import { useUserId } from '../hooks/useUserId'
import { suppliesApi, SupplyItemInput } from '../api'

type InsumoCode = 'caja' | 'bolsa_a' | 'bolsa_b' | 'sello'

const SolicitarInsumosModal: FC = () => {
  const { solicitarPrefill, closeSolicitar, bumpSupplyRefresh } = useLogisticBox()
  const idSeller = useSelectedSeller()
  const userId = useUserId()
  const isOpen = solicitarPrefill !== null

  const [cajas, setCajas] = useState(0)
  const [bolsasA, setBolsasA] = useState(0)
  const [bolsasB, setBolsasB] = useState(0)
  const [sellos, setSellos] = useState(0)
  const [chkCajas, setChkCajas] = useState(false)
  const [chkBolsasA, setChkBolsasA] = useState(false)
  const [chkBolsasB, setChkBolsasB] = useState(false)
  const [chkSellos, setChkSellos] = useState(false)
  const [notas, setNotas] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Topes (máximos) por insumo · y mensaje de la sugerencia del sistema.
  const [topes, setTopes] = useState({ caja: Infinity, bolsa_a: Infinity, bolsa_b: Infinity, sello: Infinity })
  const [cargandoReco, setCargandoReco] = useState(false)
  const [recoMsg, setRecoMsg] = useState<string | null>(null)

  // Al abrir: precarga lo sugerido y pide la recomendación al sistema (que usa
  // el stock que ya tiene la tienda · ya no se captura manualmente).
  useEffect(() => {
    if (solicitarPrefill === null) return
    setCajas(solicitarPrefill.cajas)
    setBolsasA(solicitarPrefill.bolsasA)
    setBolsasB(solicitarPrefill.bolsasB)
    setSellos(solicitarPrefill.sellos)
    setChkCajas(solicitarPrefill.cajas > 0)
    setChkBolsasA(solicitarPrefill.bolsasA > 0)
    setChkBolsasB(solicitarPrefill.bolsasB > 0)
    setChkSellos(solicitarPrefill.sellos > 0)
    setNotas('')
    setSubmitting(false)
    setTopes({ caja: Infinity, bolsa_a: Infinity, bolsa_b: Infinity, sello: Infinity })
    setRecoMsg(null)

    const idStore = solicitarPrefill.idStore
    if (idStore === undefined || idStore <= 0) return
    setCargandoReco(true)
    // Sin pasar stock → el backend usa el inventario del sistema (cantidad_actual).
    suppliesApi.recommendation(idStore, idSeller)
      .then((r) => {
        const by = new Map(r.items.map(i => [i.codigo, i]))
        const reco = (c: InsumoCode): number => by.get(c)?.recomendado ?? 0
        const tope = (c: InsumoCode): number => by.get(c)?.tope ?? Infinity
        setCajas(reco('caja')); setBolsasA(reco('bolsa_a')); setBolsasB(reco('bolsa_b')); setSellos(reco('sello'))
        setChkCajas(reco('caja') > 0); setChkBolsasA(reco('bolsa_a') > 0); setChkBolsasB(reco('bolsa_b') > 0); setChkSellos(reco('sello') > 0)
        setTopes({ caja: tope('caja'), bolsa_a: tope('bolsa_a'), bolsa_b: tope('bolsa_b'), sello: tope('sello') })
        setRecoMsg(`Demanda proyectada: ${r.equipos_proyectados} equipos (${r.equipos_base} ya recibidos + proyección del embudo). El tope por insumo es el doble de la demanda.`)
      })
      .catch((e: any) => {
        enqueueSnackbar(e?.message ?? 'No se pudo calcular la recomendación · puedes ajustar manualmente', { variant: 'warning' })
      })
      .finally(() => setCargandoReco(false))
  }, [solicitarPrefill, idSeller])

  if (!isOpen || solicitarPrefill === null) return null

  const clamp = (val: number, tope: number): number => Math.min(tope, Math.max(0, val))

  const onSubmit = async (): Promise<void> => {
    const idStore = solicitarPrefill?.idStore
    if (idStore === undefined || idStore <= 0) {
      enqueueSnackbar('No se pudo identificar la tienda de la solicitud', { variant: 'error' })
      return
    }

    const items: SupplyItemInput[] = []
    if (chkCajas && cajas > 0) items.push({ codigo: 'caja', cantidad: cajas })
    if (chkBolsasA && bolsasA > 0) items.push({ codigo: 'bolsa_a', cantidad: bolsasA })
    if (chkBolsasB && bolsasB > 0) items.push({ codigo: 'bolsa_b', cantidad: bolsasB })
    if (chkSellos && sellos > 0) items.push({ codigo: 'sello', cantidad: sellos })

    if (items.length === 0) {
      enqueueSnackbar('Selecciona al menos un insumo con cantidad mayor a 0', { variant: 'warning' })
      return
    }

    setSubmitting(true)
    try {
      const r = await suppliesApi.createRequest(
        { id_store: idStore, items, notas: notas.trim() || undefined },
        idSeller,
        userId
      )
      enqueueSnackbar(`Solicitud ${r.id_request} enviada a bodega central`, { variant: 'success' })
      bumpSupplyRefresh()
      closeSolicitar()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo crear la solicitud', { variant: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={closeSolicitar} size='lg' backdrop='blur' placement='top-center' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1 pb-2'>
          <div className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>
            Solicitud de insumos
          </div>
          <h3 className='text-sm font-bold text-[#151930]'>{solicitarPrefill.tienda}</h3>
        </ModalHeader>

        <ModalBody className='space-y-4'>
          <div className='rounded-xl p-3 text-xs border flex items-center gap-2' style={{ background: TONES.lila.bgSoft, borderColor: `${TONES.lila.ink}4d` }}>
            {cargandoReco && <Spinner size='sm' />}
            <span>
              <b style={{ color: TONES.lila.ink }}>Sugerencia del sistema: </b>
              {cargandoReco ? 'Calculando con el stock de la tienda…' : (recoMsg ?? 'Ajusta las cantidades según lo que necesites.')}
            </span>
          </div>

          <div>
            <label className='text-xs font-semibold text-[#151930]/70 uppercase tracking-wider'>¿Qué insumos solicitar?</label>
            <div className='mt-2 space-y-2'>
              <div className='flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3'>
                <Checkbox isSelected={chkCajas} onValueChange={setChkCajas} />
                <div className='flex-1'>
                  <div className='font-semibold text-sm text-[#151930]'>Cajas de recolección</div>
                  <div className='text-[14px] text-[#151930]/60'>Safety stock: {SAFETY.cajas}{isFinite(topes.caja) ? ` · máx ${topes.caja}` : ''}</div>
                </div>
                <Input
                  type='number' size='sm' className='w-24' value={String(cajas)}
                  onChange={(e) => setCajas(clamp(Number(e.target.value) || 0, topes.caja))}
                />
              </div>

              <div className='flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3'>
                <Checkbox isSelected={chkBolsasA} onValueChange={setChkBolsasA} />
                <div className='flex-1'>
                  <div className='font-semibold text-sm text-[#151930]'>Bolsas foliadas · tipo A</div>
                  <div className='text-[14px] text-[#151930]/60'>Smartphone, watch, tablet · Safety stock: {SAFETY.bolsasA}{isFinite(topes.bolsa_a) ? ` · máx ${topes.bolsa_a}` : ''}</div>
                </div>
                <Input
                  type='number' size='sm' className='w-24' value={String(bolsasA)}
                  onChange={(e) => setBolsasA(clamp(Number(e.target.value) || 0, topes.bolsa_a))}
                />
              </div>

              <div className='flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3'>
                <Checkbox isSelected={chkBolsasB} onValueChange={setChkBolsasB} />
                <div className='flex-1'>
                  <div className='font-semibold text-sm text-[#151930]'>Bolsas foliadas · tipo B</div>
                  <div className='text-[14px] text-[#151930]/60'>Consola, PC, notebook · Safety stock: {SAFETY.bolsasB}{isFinite(topes.bolsa_b) ? ` · máx ${topes.bolsa_b}` : ''}</div>
                </div>
                <Input
                  type='number' size='sm' className='w-24' value={String(bolsasB)}
                  onChange={(e) => setBolsasB(clamp(Number(e.target.value) || 0, topes.bolsa_b))}
                />
              </div>

              <div className='flex items-center gap-3 border border-gray-200 rounded-xl px-4 py-3'>
                <Checkbox isSelected={chkSellos} onValueChange={setChkSellos} />
                <div className='flex-1'>
                  <div className='font-semibold text-sm text-[#151930]'>Sellos de seguridad</div>
                  <div className='text-[14px] text-[#151930]/60'>Safety stock: {SAFETY.sellos}{isFinite(topes.sello) ? ` · máx ${topes.sello}` : ''}</div>
                </div>
                <Input
                  type='number' size='sm' className='w-24' value={String(sellos)}
                  onChange={(e) => setSellos(clamp(Number(e.target.value) || 0, topes.sello))}
                />
              </div>
            </div>
          </div>

          <Textarea
            label='Notas (opcional)'
            value={notas}
            onValueChange={setNotas}
            placeholder='Ej: necesario antes del jueves · DHL adelantó retiro'
            size='sm'
            minRows={2}
            variant='bordered'
          />
        </ModalBody>

        <ModalFooter className='flex items-center justify-end gap-2'>
          <Button variant='bordered' onPress={closeSolicitar} isDisabled={submitting} className='border-gray-200 text-[#151930]'>Cancelar</Button>
          <Button onPress={() => { void onSubmit() }} isLoading={submitting} className='bg-[#151930] text-white' endContent={<MdOutlineSend />}>
            Enviar solicitud
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SolicitarInsumosModal
